import { useAuth } from '@clerk/react'
import { useQuery } from '@tanstack/react-query'
import { ErrorApi, useApi } from '@/lib/api'

export type TipoUsuario = 'cliente' | 'rastro'

export interface UsuarioSesion {
  id: string
  nombre: string
  email: string
  tipo: TipoUsuario
}

export interface ProyectoPermitido {
  id: string
  nombre: string
  clienteId: string
  clienteNombre: string
}

export interface Sesion {
  usuario: UsuarioSesion
  proyectos: ProyectoPermitido[]
}

/**
 * Estado de la sesión que gobierna la navegación (§3, §5.4, §8.1):
 *  · `cargando`   Clerk o /api/sesion todavía no respondieron,
 *  · `anonimo`    no hay sesión de Clerk,
 *  · `revocado`   Clerk tiene sesión pero el portal la rechaza (403/401),
 *  · `lista`      usuario y proyectos permitidos resueltos.
 */
export type EstadoSesion =
  | { estado: 'cargando' }
  | { estado: 'anonimo' }
  | { estado: 'revocado' }
  | { estado: 'error'; mensaje: string }
  | ({ estado: 'lista' } & Sesion)

export const CLAVE_SESION = ['sesion'] as const

export function useSesion(): EstadoSesion {
  const { isLoaded, isSignedIn } = useAuth()
  const api = useApi()

  const consulta = useQuery({
    queryKey: CLAVE_SESION,
    queryFn: () => api<Sesion>('/api/sesion'),
    enabled: isLoaded && isSignedIn,
    staleTime: 60_000,
    retry: (intentos, error) =>
      !(error instanceof ErrorApi && error.status < 500) && intentos < 1,
  })

  if (!isLoaded) return { estado: 'cargando' }
  if (!isSignedIn) return { estado: 'anonimo' }
  if (consulta.isPending) return { estado: 'cargando' }

  if (consulta.isError) {
    const e = consulta.error
    if (e instanceof ErrorApi && (e.status === 401 || e.status === 403)) {
      return { estado: 'revocado' }
    }
    return { estado: 'error', mensaje: e instanceof Error ? e.message : 'Error' }
  }

  return { estado: 'lista', ...consulta.data }
}

/* ── Último proyecto abierto (§3: "del último visitado") ── */

const CLAVE_ULTIMO = 'rastro.ultimoProyecto'

export function leerUltimoProyecto(): string | null {
  try {
    return localStorage.getItem(CLAVE_ULTIMO)
  } catch {
    return null
  }
}

export function guardarUltimoProyecto(id: string) {
  try {
    localStorage.setItem(CLAVE_ULTIMO, id)
  } catch {
    // almacenamiento no disponible: se pierde la preferencia, nada más
  }
}

export function olvidarUltimoProyecto() {
  try {
    localStorage.removeItem(CLAVE_ULTIMO)
  } catch {
    // idem
  }
}

/**
 * §3 — a dónde va cada tipo de usuario al entrar.
 *  · RASTRO: último proyecto abierto; la primera vez, Administración.
 *  · Cliente: su proyecto (el último visitado si tiene varios); sin
 *    proyectos, se queda en la tarjeta de ingreso con el estado correcto.
 */
export function destinoInicial(sesion: Sesion): string | null {
  const ultimo = leerUltimoProyecto()
  const permitido =
    ultimo && sesion.proyectos.some((p) => p.id === ultimo) ? ultimo : null

  if (sesion.usuario.tipo === 'rastro') {
    return permitido ? `/proyecto/${permitido}` : '/admin'
  }

  const primero = sesion.proyectos[0]?.id
  if (permitido) return `/proyecto/${permitido}`
  return primero ? `/proyecto/${primero}` : null
}
